import {
  Grid,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";

import AssignmentIcon from "@mui/icons-material/Assignment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import { useTheme } from "@mui/material/styles";

import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import { getAttachmentTypes } from "../../services/AttachmentTypeService";
import {
  generatePdfFromExcel,
  uploadSignedZipForGenerated,
  downloadGeneratedZipFromExcel,
} from "../../services/LegalFileAttachmentService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import TemplateEditor from "../General/TemplateEditor";
import FileUploadBox from "../General/FileUploadBox";
import { useToast } from "../../context/ToastContext";

function SectionBlock({ icon, title, description, children }) {
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 1.5,
        borderRadius: 3,
        background:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.04)"
            : "rgba(255,255,255,0.75)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border:
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.08)",
        mt: 1,
      })}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(25,118,210,0.10)",
            color: "primary.main",
            border: "1px solid rgba(25,118,210,0.16)",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 15.5, lineHeight: 1.15 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
}

export default function GenerateUploadAttachments() {
  const { showToast } = useToast();
  const theme = useTheme();

  const fileExcelRef = useRef(null);
  const fileZipRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [typesAll, setTypesAll] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);

  const [mode, setMode] = useState("");

  const [selectedExcel, setSelectedExcel] = useState(null);
  const [selectedZip, setSelectedZip] = useState(null);
  const [templateHtml, setTemplateHtml] = useState("");

  const [openHelp, setOpenHelp] = useState(false);

  const fetchTypes = async () => {
    try {
      const res = await getAttachmentTypes();
      const data = res?.data?.data ?? [];
      setTypesAll(data);
    } catch {
      setTypesAll([]);
      setTypes([]);
      showToast("Error al cargar los tipos de escritos.", "error")
    }
  };

  useEffect(() => {
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mode) {
      setTypes([]);
      setSelectedType(null);
      return;
    }

    const filtered = typesAll.filter((t) => {
      const hasAllowGenerate = Object.prototype.hasOwnProperty.call(t, "allow_generate");
      const hasAllowSigned = Object.prototype.hasOwnProperty.call(t, "allow_signed_upload");

      if (mode === "generate" || mode === "redownload_zip") {
        return hasAllowGenerate ? !!t.allow_generate : true;
      }

      if (mode === "signed_zip") {
        if (hasAllowSigned) return !!t.allow_signed_upload;
        return hasAllowGenerate ? !!t.allow_generate : true;
      }

      return true;
    });

    setTypes(filtered);
    setSelectedType(null);
  }, [mode, typesAll]);

  useEffect(() => {
    if (selectedType) {
      setTemplateHtml(selectedType.generate_template || selectedType.text || "");
    } else {
      setTemplateHtml("");
    }
  }, [selectedType]);

  useEffect(() => {
    setSelectedExcel(null);
    setSelectedZip(null);
    if (fileExcelRef.current) fileExcelRef.current.value = "";
    if (fileZipRef.current) fileZipRef.current.value = "";
  }, [mode]);

  const handleExcelSelect = (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xls", "xlsx"].includes(ext)) {
      showToast("El archivo Excel debe ser .xls o .xlsx", "info")
      return;
    }
    setSelectedExcel(file);
  };

  const handleZipSelect = (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "zip") {
      showToast("El archivo debe ser .zip", "info")
      return;
    }
    setSelectedZip(file);
  };

  const handleRestoreTemplate = () => {
    if (selectedType) {
      setTemplateHtml(selectedType.generate_template || selectedType.text || "");
    }
  };

  const hasTypesLoaded = typesAll.length > 0;
  const hasTypesForMode = mode ? types.length > 0 : false;
  const blocked = !!mode && !hasTypesForMode;

  const canSubmit = (() => {
    if (loading) return false;
    if (!hasTypesLoaded) return false;
    if (!mode) return false;
    if (blocked) return false;
    if (!selectedType) return false;
    if (mode === "generate" && !selectedExcel) return false;
    if (mode === "signed_zip" && !selectedZip) return false;
    if (mode === "redownload_zip" && !selectedExcel) return false;
    return true;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("type_id", selectedType.id);

      if (mode === "generate" || mode === "redownload_zip") {
        formData.append("excel", selectedExcel);

        if (mode === "generate") {
          formData.append("template_html", templateHtml);
        }

        const res =
          mode === "generate"
            ? await generatePdfFromExcel(formData)
            : await downloadGeneratedZipFromExcel(formData);

        const cd = res.headers?.["content-disposition"];
        let filename =
          mode === "generate" ? "pdf_generados.zip" : "pdf_generados_existentes.zip";

        const match = cd?.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];

        const blob = new Blob([res.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        showToast(
          mode === "generate"
            ? `ZIP generado y descargado: ${filename}`
            : `ZIP descargado (ya generados): ${filename}`,
        "success")
      }

      if (mode === "signed_zip") {
        formData.append("zip", selectedZip);
        const res = await uploadSignedZipForGenerated(formData);
        showToast(
          `${res.data.message}. Procesados: ${res.data.files_processed}. Asociados: ${res.data.total_attached}.`,
        "success")
      }

      setSelectedType(null);
      setSelectedExcel(null);
      setSelectedZip(null);
      setTemplateHtml("");
      if (fileExcelRef.current) fileExcelRef.current.value = "";
      if (fileZipRef.current) fileZipRef.current.value = "";
    } catch (error) {
      console.error(error);
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        showToast(
          error.response.data.message + "\n\n• " + error.response.data.errors.join("\n• "),
        "error")
      } else {
        showToast(
          error.response?.data?.message || "Error inesperado al procesar la solicitud.",
        "error")
      }
    } finally {
      setLoading(false);
    }
  };

  const warningMessage = !hasTypesLoaded
    ? "No hay tipos de escritos cargados en el sistema. No es posible continuar."
    : "No hay tipos compatibles con la acción seleccionada.";

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          {(mode === "generate" || mode === "redownload_zip") && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Tooltip title="Ver requisitos del Excel" arrow>
                <HelpOutlineIcon
                  sx={{
                    cursor: "pointer",
                    color: theme.palette.primary.main,
                    "&:hover": { color: theme.palette.primary.dark },
                  }}
                  onClick={() => setOpenHelp(true)}
                />
              </Tooltip>
            </Box>
          )}

          <CardHeader
            icon={<AssignmentIcon />}
            title="Gestión de PDFs"
            description="1) Generá PDFs desde Excel. 2) Descargá ZIP de PDFs ya generados. 3) Subí ZIP de PDFs firmados."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {!hasTypesLoaded && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{warningMessage}</Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth disabled={!hasTypesLoaded}>
                  <InputLabel>Acción</InputLabel>
                  <Select
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    label="Acción"
                  >
                    <MenuItem value="">SELECCIONAR UNA OPCION...</MenuItem>
                    <MenuItem value="generate">GENERAR PDF'S</MenuItem>
                    <MenuItem value="redownload_zip">
                      DESCARGAR ZIP (PDF'S YA GENERADOS)
                    </MenuItem>
                    <MenuItem value="signed_zip">
                      SUBIR ZIP DE PDF'S FIRMADOS
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {blocked && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{warningMessage}</Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={types}
                  getOptionLabel={(option) =>
                    option.display_name || option.name || ""
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de escrito" fullWidth />
                  )}
                  value={selectedType}
                  onChange={(_, newValue) => setSelectedType(newValue)}
                  disabled={!hasTypesLoaded || !mode || blocked}
                />
              </Grid>

              {selectedType && mode === "generate" && (
                <Grid size={{ xs: 12 }}>
                  <SectionBlock
                    icon={<DescriptionOutlinedIcon />}
                    title="Plantilla del Escrito"
                    description="Personalizá el texto base de este envío masivo. Las etiquetas como [EXPEDIENTE] o [REEMPLAZAR], etc. se llenarán automáticamente con los datos de cada causa."
                  >
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.2 }}>
                      <Button
                        startIcon={<RefreshOutlinedIcon />}
                        onClick={handleRestoreTemplate}
                        variant="outlined"
                        size="small"
                        sx={{ borderRadius: 2.2, fontWeight: 800, textTransform: "none" }}
                      >
                        Restaurar plantilla original
                      </Button>
                    </Box>
                    <TemplateEditor value={templateHtml} onChange={setTemplateHtml} />
                  </SectionBlock>
                </Grid>
              )}

              {(mode === "generate" || mode === "redownload_zip") && (
                <Grid size={{ xs: 12 }}>
                  <FileUploadBox
                    selectedFile={selectedExcel}
                    onFileSelect={handleExcelSelect}
                    accept=".xls,.xlsx"
                    placeholder="Seleccionar Excel (Códigos de Expediente)"
                    helperText="O arrastrá el Excel aquí (.xls, .xlsx)"
                  />
                </Grid>
              )}

              {mode === "signed_zip" && (
                <Grid size={{ xs: 12 }}>
                  <FileUploadBox
                    selectedFile={selectedZip}
                    onFileSelect={handleZipSelect}
                    accept=".zip"
                    placeholder="Seleccionar ZIP (PDFs firmados)"
                    helperText="O arrastrá el archivo ZIP aquí"
                  />
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={!canSubmit}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : mode === "generate" ? (
                    "Generar PDFs"
                  ) : mode === "redownload_zip" ? (
                    "Descargar ZIP"
                  ) : (
                    "Subir Firmados"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={() => setOpenHelp(false)}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={["Código de Expediente", "Datos adicionales (opcional)"]}
        example={["12345678", "Nro de cuenta, etc."]}
      />
    </Grid>
  );
}
